import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { zodResolver } from "@hookform/resolvers/zod";
import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { z } from "zod";
import { Login } from "iconsax-react";
import { Button, Input } from "@/shared/ui";
import { HOME_PAGE_ROUTE } from "@/shared/utils";
import { useAppDispatch } from "@/shared/hooks";
import { useLoginMutation } from "@/features/auth/api";
import { setMustChangePassword, setTempToken } from "@/features/auth";
import type { LoginErrorResponse } from "@/features/auth/types";

const demoLoginSchema = z.object({
  identifier: z.string().min(1, "login.identifier.error.required"),
  password: z.string().min(1, "login.password.error.required"),
});

type DemoLoginFormValues = z.infer<typeof demoLoginSchema>;

interface Props {
  onSwitchToCreate: () => void;
}

function getLoginErrorKey(error: unknown): string {
  if (error && typeof error === "object" && "data" in error) {
    const fetchError = error as FetchBaseQueryError;
    if (typeof fetchError.status === "number" && fetchError.status === 401) {
      return "login.errors.invalidCredentials";
    }
    if (typeof fetchError.status === "number" && fetchError.status === 429) {
      return "login.errors.rateLimited";
    }
    if (fetchError.data && typeof fetchError.data === "object") {
      const errorData = fetchError.data as LoginErrorResponse;
      if (errorData.error === "Account is disabled") {
        return "login.errors.accountDisabled";
      }
    }
    if (fetchError.status === "FETCH_ERROR" || fetchError.status === "PARSING_ERROR") {
      return "login.errors.serverUnavailable";
    }
    if (typeof fetchError.status === "number" && fetchError.status >= 500) {
      return "login.errors.serverUnavailable";
    }
  }
  return "login.errors.unknown";
}

export default function DemoLoginForm({ onSwitchToCreate }: Props) {
  const { t } = useTranslation("DemoPage");
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [login, { isLoading, error }] = useLoginMutation();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<DemoLoginFormValues>({
    resolver: zodResolver(demoLoginSchema),
  });

  async function onSubmit(data: DemoLoginFormValues) {
    try {
      const res = await login(data).unwrap();
      if (res.require_password_change && res.temp_token) {
        dispatch(setTempToken(res.temp_token));
        dispatch(setMustChangePassword(true));
      } else {
        dispatch(setTempToken(null));
        dispatch(setMustChangePassword(false));
      }
      navigate(HOME_PAGE_ROUTE, { replace: true });
    } catch {
      // error state handled by RTK Query
    }
  }

  return (
    <div className="flex-1 flex items-center justify-center p-5 pt-0">
      <div className="surface-base-fill radius-3xl p-7 w-full max-w-xl flex flex-col gap-6">
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 radius-md surface-brand-subtle content-action-brand flex items-center justify-center shrink-0">
              <Login size={20} color="currentColor" />
            </div>
            <h1 className="text-display-2xs content-base-primary">{t("login.title")}</h1>
          </div>
          <p className="text-body-regular-sm content-base-secondary">{t("login.subtitle")}</p>
        </div>

        <form className="flex flex-col gap-4" onSubmit={handleSubmit(onSubmit)}>
          <Input
            label={t("login.identifier.label")}
            placeholder={t("login.identifier.placeholder")}
            autoComplete="username"
            error={errors.identifier?.message ? t(errors.identifier.message) : undefined}
            {...register("identifier")}
          />
          <Input
            type="password"
            label={t("login.password.label")}
            placeholder={t("login.password.placeholder")}
            autoComplete="current-password"
            error={errors.password?.message ? t(errors.password.message) : undefined}
            {...register("password")}
          />

          {error && !isLoading && (
            <p className="text-body-regular-sm content-action-negative">{t(getLoginErrorKey(error))}</p>
          )}

          <Button variant="primary" size="huge" type="submit" disabled={isLoading}>
            {isLoading ? t("login.loading") : t("login.button")}
          </Button>
        </form>

        <div className="text-center">
          <button
            type="button"
            className="text-body-regular-sm content-action-brand cursor-pointer outline-none focus-visible:ring-4 focus-visible:ring-offset-2 focus-visible:ring-brand-200 radius-xs"
            onClick={onSwitchToCreate}>
            {t("login.switchToCreate")}
          </button>
        </div>
      </div>
    </div>
  );
}
